import { useIntl, defineMessages } from 'react-intl';
import { resolveImageURL } from 'volto-essvoltocomponents/helpers';
import {
  ContentWrapper,
  PageSection,
  ContentNarrow,
  ContentWide,
} from 'volto-essvoltocomponents/components/Layout';
import PageHeaderBackground from '../../Molecules/PageHeaderBackground';
import RichText from '../../Atoms/RichText';
import { validateType } from '../../../helpers/contentValidators';
import Grid from '../../Atoms/Grid/Grid';
import ResponsiveComponentSwitch from '../../Atoms/ResponsiveComponentSwitch';
import ResponsiveGrid from '../../Atoms/ResponsiveGrid';
import Carousel from '../../Atoms/Carousel';
import TeaserCardVertical from '../../Molecules/TeaserCard/TeaserCardVertical';
import { ESSButton } from 'volto-essvoltocomponents/components/Atoms';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import './TeamShowcaseView.scss';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const GRID_BREAKPOINTS = [
  { minWidth: 0, columns: 1 },
  { minWidth: 768, columns: 2 },
  { minWidth: 1440, columns: 4 },
];

const PAGE_SECTIONS = [
  { id: 'section-about', title: 'What we do' },
  { id: 'section-methodologies', title: 'How we do this' },
  { id: 'section-relevance', title: 'Related projects & services' },
];

const messages = defineMessages({
  relevanceExploreMore: {
    id: 'relavance.exploreMore',
    defaultMessage: 'VIEW ALL OUR WORK',
  },
});
const EMPTY_ARRAY = [];

// ---------------------------------------------------------------------------
// TeamShowcaseView
// ---------------------------------------------------------------------------

const TeamShowcaseView = ({ content }) => {
  const intl = useIntl();
  const title = content.title;
  const titleBgImageUrl = resolveImageURL(content, '/@@images/hero_image/hero');
  const aboutThumbnailUrl = resolveImageURL(
    content,
    '/@@images/thumbnail_image/preview',
  );
  const aboutImageUrl = resolveImageURL(
    content,
    '/@@images/what_we_do_image/larger',
  );
  const fullDescription = content.full_description?.data;
  const about = content?.what_we_do_description?.data;
  const methodologies = content?.how_we_do_it_description?.data;

  // "work" relationlist validation
  const relatedWorkList = content?.highlighted_work || EMPTY_ARRAY;
  const relatedWorkItems = relatedWorkList
    .filter((item) =>
      validateType(item, ['project', 'sector', 'service', 'competence']),
    )
    .slice(0, 4);
  const relatedWorkHasItems = relatedWorkItems.length > 0;

  // related_work_items Card renderer (identical to Resources_items
  const relatedWorkCardElements = relatedWorkItems.map((item, index) => {
    return (
      <TeaserCardVertical
        key={item['@id'] || index}
        title={item.title || ''}
        description={item?.short_description?.data || ''}
        imageUrl={resolveImageURL(item, '/@@images/thumbnail_image/preview')}
        url={item['@id'] || ''}
      />
    );
  });

  const availableSections = PAGE_SECTIONS.filter((section, index) => {
    switch (section.id) {
      case 'section-about':
        return !!about;
      case 'section-methodologies':
        return !!methodologies;
      case 'section-relevance':
        return relatedWorkHasItems;
      default:
        return false;
    }
  });

  // Build inline styles for custom colors and border radius
  const detailStyles = {
    display: 'flex',
    flexFlow: 'row wrap',
  };

  const borderStyle = 'solid 1px var(--color-neutral-01)';

  const aboutText = (
    <div className="team-showcase-view__about-text">
      <h2>{PAGE_SECTIONS[0].title}</h2>
      <RichText content={about} />
    </div>
  );

  return (
    <>
      <PageHeaderBackground
        title={title}
        description={fullDescription}
        backgroundImage={titleBgImageUrl}
      />

      {/* Menu */}
      <PageSection
        id={availableSections[0].id}
        paddingTop="var(--spacing-3)"
        paddingBottom="var(--spacing-3)"
        borderTop={borderStyle}
      >
        <ContentWrapper className="team-showcase-view-menu-wrapper">
          <ContentWide
            className="team-showcase-view-menu"
            style={{ flexDirection: 'row', flexWrap: 'wrap' }}
          >
            {availableSections.map((section) => (
              <ESSButton
                variant="link-icon"
                key={section.id}
                onClick={() =>
                  document.getElementById(section.id)?.scrollIntoView({
                    behavior: 'smooth',
                  })
                }
              >
                {section.title}
              </ESSButton>
            ))}
          </ContentWide>
        </ContentWrapper>
      </PageSection>

      {/* About */}
      {about && (
        <PageSection
          className="team-showcase-view__about-section"
          id={PAGE_SECTIONS[0].id}
          borderTop={borderStyle}
        >
          <ContentWrapper>
            <ContentWide>
              <ContentNarrow>
                {/* Responsive component switch showing a Grid with 2 columns (a text container, on the left col, and an image, on the right) for 768px and only the text container (in full width) for narrower screens */}
                <ResponsiveComponentSwitch
                  breakpoints={[
                    {
                      minWidth: 768,
                      component: (
                        <Grid cols="3fr 1fr">
                          {aboutText}
                          {aboutThumbnailUrl && (
                            <img
                              src={aboutThumbnailUrl}
                              alt={content.title || ''}
                              loading="lazy"
                              width="100%"
                            />
                          )}
                        </Grid>
                      ),
                    },
                  ]}
                  defaultComponent={aboutText}
                />

                {aboutImageUrl && (
                  <div style={{ justifySelf: 'center' }}>
                    <img
                      src={aboutImageUrl}
                      alt={content.title || ''}
                      loading="lazy"
                    />
                  </div>
                )}
              </ContentNarrow>
            </ContentWide>
          </ContentWrapper>
        </PageSection>
      )}

      {/* Relevance */}
      {methodologies && (
        <PageSection id={PAGE_SECTIONS[1].id} paddingBottom="0">
          <ContentWrapper className="team-showcase-view-about-wrapper">
            <ContentWide>
              <ContentNarrow className="team-showcase-view-about-title">
                <h2>{PAGE_SECTIONS[1].title}</h2>
              </ContentNarrow>
              <ContentNarrow className="team-showcase-view-about-content">
                <RichText content={methodologies} />
              </ContentNarrow>
            </ContentWide>
          </ContentWrapper>
        </PageSection>
      )}

      <PageSection
        paddingTop="var(--page-section-paddingtb)"
        paddingBottom="0"
      />

      {/* Related projects and sectors */}
      {relatedWorkHasItems && (
        <PageSection
          id={PAGE_SECTIONS[2].id}
          className="team-showcase-view__work-section"
          paddingBottom="0"
          borderTop={borderStyle}
        >
          <ContentWrapper>
            <ContentWide>
              <h2>{PAGE_SECTIONS[2].title}</h2>
              <p>Discover the projects and services that support our work.</p>
              {/* Responsive grid: carousel on mobile, grid on desktop s*/}
              <ResponsiveComponentSwitch
                breakpoints={[
                  {
                    minWidth: 768,
                    component: (
                      <ResponsiveGrid breakpoints={GRID_BREAKPOINTS}>
                        {relatedWorkCardElements}
                      </ResponsiveGrid>
                    ),
                  },
                ]}
                defaultComponent={
                  <Carousel>{relatedWorkCardElements}</Carousel>
                }
              />
              <div className="team-showcase-view__cta-link" alignment="center">
                <ESSButton
                  variant="link-icon"
                  className="team-showcase-view__explore-link"
                  href="/our-work"
                >
                  {intl.formatMessage(messages.relevanceExploreMore)}
                  <FontAwesomeIcon icon={faChevronRight} size="sm" />
                </ESSButton>
              </div>
            </ContentWide>
          </ContentWrapper>
        </PageSection>
      )}

      <PageSection paddingTop="var(--ess-space-7xl)" paddingBottom="0" />
    </>
  );
};

export default TeamShowcaseView;
